/**
 * Show hours by date (no-sale alerts, owner 22 Oct 2026; CONTRACT §1).
 *
 * The form keeps one flat row per show date; the event API gets
 * Events.schedule: [{ date: 'YYYY-MM-DD', dayOff, time?: { from, to } }]
 * with from/to in ms from local midnight. weeklySchedule is never written:
 * it only pre-fills a show that has no per-date hours yet.
 */
import {
  weekdayOf,
  ParsedShowDay,
  formatShowDate,
} from 'utils/show-hours-parser';
import type {
  WeeklySchedule,
  EventScheduleEntry as ScheduleEntry,
  EventScheduleFormRow as ScheduleFormRow,
} from 'store/api/event/types';

export type { ScheduleEntry, ScheduleFormRow };

/** Rows the reader flagged: "unclear" (couldn't read) or "unmentioned". */
export type ScheduleFlags = Record<
  string,
  { reason: string; kind: 'unclear' | 'unmentioned' }
>;

// Dates more than this many days apart are a typo: list no rows. 62 days
// apart (63 dates) still lists; 63 days apart does not (form message too).
export const MAX_SHOW_DAYS_APART = 62;

const WEEKDAY_NAMES = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * The calendar day of a form date (a Date from the picker, '10/24/2026'
 * from formatDate, or '2026-10-24…' from the API) as YYYY-MM-DD.
 */
export const toIsoDate = (value: unknown): string | undefined => {
  if (!value) {
    return undefined;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? undefined
      : `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
  const text = String(value);
  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return `${iso[1]}-${iso[2]}-${iso[3]}`;
  }
  const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (us) {
    return `${us[3]}-${pad(Number(us[1]))}-${pad(Number(us[2]))}`;
  }
  return undefined;
};

/**
 * Every date from start to end, inclusive: none if either is missing, the
 * end is before the start, or they are more than MAX_SHOW_DAYS_APART days
 * apart.
 */
export const listShowDates = (start: unknown, end: unknown): string[] => {
  const from = toIsoDate(start);
  const to = toIsoDate(end);
  if (!from || !to || to < from) {
    return [];
  }
  const first = Date.parse(`${from}T00:00:00Z`);
  const apart = Math.round(
    (Date.parse(`${to}T00:00:00Z`) - first) / 86_400_000
  );
  if (apart > MAX_SHOW_DAYS_APART) {
    return [];
  }
  return Array.from({ length: apart + 1 }, (_, i) =>
    new Date(first + i * 86_400_000).toISOString().slice(0, 10)
  );
};

export const showDateLabel = formatShowDate;

const isMs = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** The API's schedule as form rows by date (tolerates old shapes). */
const savedRowsByDate = (saved: unknown) => {
  const byDate = new Map<string, ScheduleFormRow>();
  if (!Array.isArray(saved)) {
    return byDate;
  }
  saved.forEach((entry) => {
    const date = toIsoDate(entry?.date);
    if (!date) {
      return;
    }
    const from = entry?.time?.from ?? entry?.from;
    const to = entry?.time?.to ?? entry?.to;
    byDate.set(date, {
      date,
      dayOff: Boolean(entry?.dayOff),
      ...(isMs(from) ? { from } : {}),
      ...(isMs(to) ? { to } : {}),
    });
  });
  return byDate;
};

const weeklyHasHours = (weekly?: WeeklySchedule[] | null) =>
  Boolean(weekly?.some((day) => isMs(day?.from) && isMs(day?.to)));

/**
 * One row per show date. A date keeps the values it already has (`current`
 * first, then the saved schedule); a show with no per-date hours yet takes
 * its weekday's hours from the old weekly schedule.
 */
export const buildScheduleRows = ({
  dates,
  saved,
  weekly,
  current,
}: {
  dates: string[];
  saved?: unknown;
  current?: ScheduleFormRow[];
  weekly?: WeeklySchedule[] | null;
}): ScheduleFormRow[] => {
  const savedByDate = savedRowsByDate(saved);
  const currentByDate = new Map(
    (current ?? []).map((row) => [row.date, row] as const)
  );
  const useWeekly = !savedByDate.size && weeklyHasHours(weekly);

  return dates.map((date) => {
    const kept = currentByDate.get(date) ?? savedByDate.get(date);
    if (kept) {
      return { ...kept, date };
    }
    if (useWeekly) {
      const name = WEEKDAY_NAMES[weekdayOf(date)];
      const day = weekly?.find((item) => item?.day?.toLowerCase() === name);
      if (day && isMs(day.from) && isMs(day.to)) {
        return { date, to: day.to, dayOff: false, from: day.from };
      }
    }
    return { date, dayOff: false };
  });
};

/** CONTRACT §1 shape: closed → { dayOff: true }; both times → time. */
export const scheduleForSubmit = (
  rows: ScheduleFormRow[] | undefined
): ScheduleEntry[] =>
  (rows ?? []).map(({ to, date, from, dayOff }) => {
    if (dayOff) {
      return { date, dayOff: true };
    }
    if (isMs(from) && isMs(to)) {
      return { date, dayOff: false, time: { to, from } };
    }
    return { date, dayOff: false };
  });

/**
 * A row holding something: a time or Closed. An empty row was not kept by
 * hand ("Copy first day to all" from an empty first day, Closed ticked and
 * unticked): the text box still fills it (owner, 30 Oct).
 */
export const rowHasHours = (row?: ScheduleFormRow) =>
  Boolean(row && (row.dayOff || isMs(row.from) || isMs(row.to)));

/** The first row's hours (or Closed) on every date. */
export const copyFirstToAll = (rows: ScheduleFormRow[]) => {
  const [first] = rows;
  if (!first) {
    return rows;
  }
  return rows.map((row) => ({
    date: row.date,
    dayOff: first.dayOff,
    ...(first.dayOff || !isMs(first.from) ? {} : { from: first.from }),
    ...(first.dayOff || !isMs(first.to) ? {} : { to: first.to }),
  }));
};

/**
 * Puts what the reader found into the rows. A date the text doesn't
 * mention keeps its values and is flagged "Closed?"; a date it couldn't
 * read (overnight, unreadable) keeps its From and To untouched and is
 * flagged.
 */
export const applyParsedDays = (
  rows: ScheduleFormRow[],
  days: ParsedShowDay[]
): { flags: ScheduleFlags; rows: ScheduleFormRow[] } => {
  const byDate = new Map(days.map((day) => [day.date, day] as const));
  const flags: ScheduleFlags = {};
  const next = rows.map((row) => {
    const day = byDate.get(row.date);
    if (!day || day.status === 'unmentioned') {
      if (!row.dayOff) {
        flags[row.date] = {
          kind: 'unmentioned',
          reason: 'Not in the pasted text. Closed?',
        };
      }
      return row;
    }
    if (day.status === 'unclear') {
      flags[row.date] = {
        kind: 'unclear',
        reason: day.reason ?? 'Hours not understood',
      };
      // Keep the row exactly as it was (Dev 23 Oct: an overnight paste set
      // From to 6 pm and left the old To): only the yellow message changes.
      return row;
    }
    if (day.dayOff) {
      return { dayOff: true, date: row.date };
    }
    return { to: day.to, dayOff: false, date: row.date, from: day.from };
  });
  return { flags, rows: next };
};

/**
 * Notes after adding the reader's setup/teardown lines: appended on new
 * lines, never replacing what is there, never twice.
 */
export const appendNotes = (notes: string | undefined, lines: string[]) => {
  const current = notes ?? '';
  const fresh = lines.filter((line) => line && !current.includes(line));
  if (!fresh.length) {
    return current;
  }
  return [current.trimEnd(), ...fresh].filter(Boolean).join('\n');
};

/** Row errors for the form: To after From unless Closed; both or neither. */
export const scheduleRowError = (row: ScheduleFormRow) => {
  if (row.dayOff) {
    return undefined;
  }
  const hasFrom = isMs(row.from);
  const hasTo = isMs(row.to);
  if (hasFrom !== hasTo) {
    return hasFrom ? 'Add the closing time' : 'Add the opening time';
  }
  if (hasFrom && hasTo && Number(row.to) <= Number(row.from)) {
    return 'Closing time must be after the opening time';
  }
  return undefined;
};

const rowsKey = (rows: ScheduleFormRow[] | undefined) =>
  JSON.stringify(scheduleForSubmit(rows));

/**
 * What the show form sends for show hours (owner must-fix, 23 Oct: never
 * wipe saved hours). Nothing — so the saved schedule stays exactly as it
 * is — unless a row was changed, pasted or copied and the rows now differ
 * from what was loaded. A show whose dates list no rows (> 2 months) never
 * sends any.
 */
export const schedulePatch = ({
  rows,
  touched,
  initialRows,
}: {
  touched?: boolean;
  rows?: ScheduleFormRow[];
  initialRows?: ScheduleFormRow[];
}): { schedule?: ScheduleEntry[] } => {
  if (!touched || !rows?.length || rowsKey(rows) === rowsKey(initialRows)) {
    return {};
  }
  return { schedule: scheduleForSubmit(rows) };
};

/** "Check the yellow show hours (Fri 23 Oct, Sat 24 Oct)". */
export const unclearMessage = (dates: string[]) => {
  const shown = dates.slice(0, 3).map(formatShowDate).join(', ');
  const more = dates.length > 3 ? ` +${dates.length - 3} more` : '';
  return `Check the yellow show hours (${shown}${more})`;
};

/** "Fix the times in red (Fri 6 Nov)": Save waits for a refused time. */
export const refusedMessage = (dates: string[]) => {
  const shown = dates.slice(0, 3).map(formatShowDate).join(', ');
  const more = dates.length > 3 ? ` +${dates.length - 3} more` : '';
  return `Fix the times in red (${shown}${more})`;
};

/** "10:00 am": the time pickers' label for ms from midnight. */
export const formatClock = (ms: number) => {
  const minutes = Math.round(ms / 60_000);
  const hours = Math.floor(minutes / 60) % 24;
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${String(minutes % 60).padStart(2, '0')} ${hours < 12 ? 'am' : 'pm'}`;
};

/**
 * What "Keep …" keeps on a yellow row: its current hours, "Closed", or
 * nothing (no link) when it has no hours to keep.
 */
export const keepLabel = (row: ScheduleFormRow) => {
  if (row.dayOff) {
    return 'Keep Closed';
  }
  if (isMs(row.from) && isMs(row.to)) {
    return `Keep ${formatClock(row.from)} – ${formatClock(row.to)}`;
  }
  return undefined;
};
